import { useCallback, useEffect, useMemo, useState } from 'react';
import { Copy, Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { courseKeysApi } from '@/lib/api/courseKeys';
import { formatEnrollmentStartDate, getAdminBusinessEtStacked } from '@/lib/admin-datetime';
import type {
  CourseKeyConfig,
  CourseKeyCourseVersionStat,
  CourseKeyEnrollmentRow,
  IspringEnrollmentRow,
} from '@/types/course-key';

export type CourseKeyUsersSheetTarget = {
  config: CourseKeyConfig;
  /** When set, only iSpring students on this course-mapping version are shown. */
  courseVersion?: number | null;
  versionStat?: CourseKeyCourseVersionStat | null;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: CourseKeyUsersSheetTarget | null;
};

function programTitle(config: CourseKeyConfig): string {
  if (typeof config.programId === 'object' && config.programId?.title) {
    return config.programId.title;
  }
  return String(config.programId);
}

function formatDeliveryMode(mode: string | null | undefined): string {
  if (!mode?.trim()) return '—';
  const kebab = mode.trim().toLowerCase();
  const labels: Record<string, string> = {
    hybrid: 'Hybrid',
    online: 'Online',
    'via-zoom': 'Via-Zoom',
    'on-campus': 'On-Campus',
    oncampus: 'On-Campus',
    zoom: 'Via-Zoom',
  };
  return labels[kebab] || mode;
}

function studentPhone(row: CourseKeyEnrollmentRow): string {
  return row.cellPhoneNumber || row.phone || '';
}

function StackedDateCell({ value }: { value: Date | string | number | null | undefined }) {
  const stacked = getAdminBusinessEtStacked(value);
  if (!stacked) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }
  return (
    <div className="text-sm leading-tight text-muted-foreground">
      <div>{stacked.dateLine}</div>
      <div className="pt-1 text-xs">{stacked.timeLine}</div>
    </div>
  );
}

function isIspringRow(row: CourseKeyEnrollmentRow): row is IspringEnrollmentRow {
  return row.platform === 'ispring';
}

export function CourseKeyUsersSheet({ open, onOpenChange, target }: Props) {
  const [rows, setRows] = useState<CourseKeyEnrollmentRow[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!target?.config._id) return;
    setLoading(true);
    try {
      const data = await courseKeysApi.listConfigEnrollments(target.config._id);
      setRows(data || []);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load students');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [target?.config._id]);

  useEffect(() => {
    if (!open || !target?.config._id) return;
    void load();
  }, [open, target?.config._id, load]);

  const filteredRows = useMemo(() => {
    if (!target) return [];
    const { config, courseVersion } = target;

    if (config.platform === 'mindtap') {
      return rows.filter((row) => row.platform === 'mindtap');
    }

    return rows.filter((row): row is IspringEnrollmentRow => {
      if (!isIspringRow(row) || !row.isProvisioned) return false;
      if (courseVersion == null) return true;
      return row.ispringCourseVersion === courseVersion;
    });
  }, [rows, target]);

  const handleCopyContact = async (row: CourseKeyEnrollmentRow) => {
    const phone = studentPhone(row);
    const text = [row.studentName, row.email, phone].filter(Boolean).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Contact copied');
    } catch {
      toast.error('Could not copy contact');
    }
  };

  const title = target ? programTitle(target.config) : 'Students';
  const versionLabel =
    target?.courseVersion != null
      ? `Course mapping v${target.courseVersion}${
          target.versionStat?.isCurrent ? ' · current' : ''
        }`
      : target?.config.platform === 'ispring'
        ? 'All provisioned iSpring students'
        : 'MindTap enrollments';
  const courseCount = target?.versionStat?.ispringCourseIds.length;
  const courseCountLabel =
    courseCount != null
      ? courseCount === 1
        ? '1 mapped course'
        : `${courseCount} mapped courses`
      : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-xl"
      >
        <div className="shrink-0 border-b border-border bg-background px-6 pb-5 pt-6 pr-14">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0 flex-1 space-y-2">
              <SheetTitle className="text-lg font-semibold leading-tight">
                Course key students
              </SheetTitle>
              <SheetDescription asChild>
                <div className="space-y-1 text-sm">
                  <p className="font-medium text-foreground">{title}</p>
                  <p className="text-muted-foreground">{versionLabel}</p>
                  {courseCountLabel ? (
                    <p className="text-muted-foreground">{courseCountLabel}</p>
                  ) : null}
                </div>
              </SheetDescription>
              <Badge variant="secondary" className="font-normal">
                {filteredRows.length} student{filteredRows.length === 1 ? '' : 's'}
              </Badge>
            </div>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col px-6 py-5">
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm">
            {loading ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-20 text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin" />
                <p className="text-sm">Loading students…</p>
              </div>
            ) : filteredRows.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-20 text-center">
                <Users className="h-8 w-8 text-muted-foreground/50" />
                <p className="text-sm font-medium text-foreground">No students</p>
                <p className="max-w-[260px] text-sm text-muted-foreground">
                  {target?.config.platform === 'ispring'
                    ? 'No provisioned students are on this course mapping yet.'
                    : 'No enrollments were found for this course key.'}
                </p>
              </div>
            ) : (
              <div className="min-h-0 flex-1 overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="h-11 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Student
                      </TableHead>
                      <TableHead className="h-11 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Start
                      </TableHead>
                      <TableHead className="h-11 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Status
                      </TableHead>
                      <TableHead className="h-11 w-12 px-2" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRows.map((row) => {
                      const ispring = isIspringRow(row) ? row : null;
                      return (
                        <TableRow key={row.orderId} className="hover:bg-muted/40">
                          <TableCell className="px-4 py-4 align-top">
                            <div className="space-y-0.5">
                              <p className="font-medium leading-snug text-foreground">
                                {row.studentName || '—'}
                              </p>
                              <p className="text-xs leading-relaxed text-muted-foreground">
                                {row.email || '—'}
                              </p>
                              {studentPhone(row) ? (
                                <p className="text-xs text-muted-foreground">
                                  {studentPhone(row)}
                                </p>
                              ) : null}
                              <p className="text-xs text-muted-foreground">
                                {formatDeliveryMode(row.deliveryMode)}
                              </p>
                              {ispring?.ispringCourseVersion != null ? (
                                <Badge variant="outline" className="mt-1 font-normal">
                                  v{ispring.ispringCourseVersion}
                                  {ispring.isCurrentCourseVersion ? ' · current' : ''}
                                </Badge>
                              ) : null}
                            </div>
                          </TableCell>
                          <TableCell className="px-4 py-4 align-top text-sm whitespace-nowrap text-muted-foreground">
                            {formatEnrollmentStartDate(row.startDate)}
                          </TableCell>
                          <TableCell className="px-4 py-4 align-top">
                            {ispring ? (
                              <div className="space-y-1">
                                <p className="text-sm capitalize text-foreground">
                                  {ispring.accessStatus.replace(/_/g, ' ')}
                                </p>
                                <StackedDateCell value={ispring.provisionedAt} />
                              </div>
                            ) : (
                              <p className="text-sm capitalize text-foreground">
                                {row.platform === 'mindtap' ? row.releaseStatus : '—'}
                              </p>
                            )}
                          </TableCell>
                          <TableCell className="px-2 py-4 align-top">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-foreground"
                              aria-label={`Copy contact for ${row.studentName || row.email}`}
                              onClick={() => void handleCopyContact(row)}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
